import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import Header from './Header';
import StepNode from './StepNode';
import ReactFlow, {
  Background,
  Controls,
  MiniMap,
  ReactFlowProvider,
  EdgeLabelRenderer,
  SelectionMode,
  applyNodeChanges,
  applyEdgeChanges,
  addEdge,
  useReactFlow,
} from 'reactflow';
import 'reactflow/dist/style.css';
import useProjectLogic from './logics/useProjectLogic';
import { FaTrash, FaMoon, FaSun } from 'react-icons/fa';
import StepEditor from './StepEditor/StepEditor';
import { v4 as uuidv4 } from 'uuid';
import { supabase } from '../../../supabase';
import Loading from '../../../components/Loading';
import ConfigFunnelModal from '../../../components/ConfigFunnelModal';
import ConfigFunnelDesign from './components/ConfigFunnelDesign';
import { useNavigate } from 'react-router-dom';

const StepNodeWithDarkMode = (props) => (
  <StepNode {...props} isDarkMode={props.data.isDarkMode} />
);

const nodeTypes = { step: StepNodeWithDarkMode };

// Optional: example for styling selected edge
function getStyledEdges(edges, selectedEdge) {
  return edges.map(edge =>
    edge.id === selectedEdge
      ? { ...edge, style: { stroke: '#a21caf', strokeWidth: 4, zIndex: 100000 } }
      : { ...edge, style: { stroke: '#2563eb', strokeWidth: 3, cursor: 'crosshair', zIndex: 1000000 } }
  );
}

// Component that has access to ReactFlow context
const ReactFlowCanvas = ({ 
  nodes, 
  edges, 
  onNodesChange, 
  onConnect, 
  nodeTypes, 
  onHandleClick, 
  onEdgeClick, 
  selectedNodeId, 
  setSelectedNodeId, 
  setEditingNodeId, 
  setConfigPanelOpen, 
  handleEditNode, 
  selectedEdge, 
  handleDeleteEdge, 
  isDarkMode,
  setNodes,
  removeNode,
  updateNode,
  duplicateNode
}) => {
  const reactFlowInstance = useReactFlow();

  const addStep = () => {
    // Convert absolute screen coordinates to ReactFlow coordinates
    const buttonScreenPosition = { x: 10 + 100, y: 80 + 50 }; // Approximate center of button
    const flowPosition = reactFlowInstance.screenToFlowPosition(buttonScreenPosition);
    
    // Create a new node with a unique id and default data
    const newNodeId = uuidv4();
    const newNode = {
      id: 'step-'+newNodeId,
      type: 'step',
      position: flowPosition,
      data: {
        id: 'step-'+newNodeId,
        title: 'Nova Etapa',
        blocks: [],
        source: null,
        // Use the same function interface as existing nodes
        duplicateNode,
        removeNode,
        setNodes,
        setEditingNodeId,
        setConfigPanelOpen,
        isDarkMode
      }
    };
    // Add the new node to the funnel
    setNodes(prevNodes => [...prevNodes, newNode]);
    setConfigPanelOpen(false);
    setEditingNodeId('step-'+newNodeId);
    setSelectedNodeId('step-'+newNodeId);
  };

  // Expose addStep to window so the button can call it
  useEffect(() => {
    window.addStep = addStep;
    return () => {
      delete window.addStep;
    };
  }, [isDarkMode, setNodes, removeNode, updateNode, duplicateNode, setEditingNodeId, setConfigPanelOpen]);

  return (
    <ReactFlow
      nodes={nodes}
      edges={getStyledEdges(edges, selectedEdge)}
      onNodesChange={onNodesChange}
      onConnect={onConnect}
      nodeTypes={nodeTypes}
      onHandleClick={onHandleClick}
      onEdgeClick={onEdgeClick}
      panOnDrag={[1, 2]} // Only allow panning with right or middle mouse button
      panOnScroll={false}
      selectionOnDrag={true}
      selectedNodes={selectedNodeId}
      onNodeClick={(event, node) => {
        setSelectedNodeId([node.id]);
      }}
      onNodeDoubleClick={(event, node) => {
        if(node.id === 'start-step'){
          return;
        }
        setEditingNodeId(null); // Close any open StepEditor
        setConfigPanelOpen(false);
        setTimeout(() => handleEditNode(node.id), 0); // Open the new one after closing
      }}
      fitView
      minZoom={0.1}
    >
      <Background color={isDarkMode ? '#374151' : '#aaa'} />
      <Controls />
    </ReactFlow>
  );
};

export default function FunnelEditor() {

  const [user, setUser] = useState(null);
  const navigate = useNavigate();
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const saved = localStorage.getItem('funnelEditor-darkMode');
    return saved ? JSON.parse(saved) : false;
  });
  const [script, setScript] = useState(null);
  const [isPublishing, setIsPublishing] = useState(false);

  // Save dark mode preference to localStorage
  useEffect(() => {
    localStorage.setItem('funnelEditor-darkMode', JSON.stringify(isDarkMode));
  }, [isDarkMode]);

  // Update nodes with isDarkMode when it changes
  useEffect(() => {
    setNodes(prevNodes => prevNodes.map(node => ({
      ...node,
      data: {
        ...node.data,
        isDarkMode
      }
    })));
  }, [isDarkMode]);

  useEffect(() => {
    supabase.auth.getUser().then(({ data, error }) => {
      if (error || !data.user) {
        navigate('/login');
      } else {
        setUser(data.user);
      }
    });
  }, []);

  const toggleDarkMode = () => {
    setIsDarkMode(!isDarkMode);
  };

  const [editingNodeId, setEditingNodeId] = useState(null);
  const [configPanelOpen, setConfigPanelOpen] = useState(false);
  const [nodes, setNodes] = useState([]);
  const [edges, setEdges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [edgeCenter, setEdgeCenter] = useState(null);
  const [selectedEdge, setSelectedEdge] = useState(null);
  const [selectedNodeId, setSelectedNodeId] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [nodeToDelete, setNodeToDelete] = useState(null);
  const [funnel, setFunnel] = useState(null);
  const [settingsFunnel, setSettingsFunnel] = useState(null);
  const [showConfigFunnelModal, setShowConfigFunnelModal] = useState(false);
  const [showConfigFunnelDesign, setShowConfigFunnelDesign] = useState(false);

  const { id } = useParams();
  const reactFlowWrapper = useRef(null);

  // Define functions with useCallback
  const removeNode = useCallback((nodeId) => {
   
    setNodeToDelete(nodeId);
    setShowDeleteModal(true);
  }, []);

  const confirmDelete = useCallback(() => {
    if (nodeToDelete) {
      setEdges(eds => eds.filter(e => e.source !== nodeToDelete && e.target !== nodeToDelete));
      setNodes(nodes => nodes.filter(n => n.id !== nodeToDelete));
      setEditingNodeId(null);
      setConfigPanelOpen(false);
    }
    setShowDeleteModal(false);
    setNodeToDelete(null);
  }, [nodeToDelete]);

  const cancelDelete = useCallback(() => {
    setShowDeleteModal(false);
    setNodeToDelete(null);
  }, []);

  const duplicateNode = useCallback((nodeId) => {
    let newNodeId = null;
    let newTitle = null;
    setNodes(nodes => {
      const nodeToDup = nodes.find(n => n.id === nodeId);
      if (!nodeToDup) return nodes;

      const newId = 'step-' + uuidv4();
      newNodeId = newId;
      newTitle = 'Cópia de ' + nodeToDup.data.title;
      // Deep clone blocks and assign new ids to blocks and options
      function cloneBlocks(blocks) {
        if (!Array.isArray(blocks)) return [];
        return blocks.map(block => {
          const newBlockId = 'block-' + uuidv4();
          if (block.type === 'quiz' && Array.isArray(block.options)) {
            return {
              ...block,
              id: newBlockId,
              options: block.options.map(opt => ({
                ...opt,
                id: 'option-' + uuidv4()
              }))
            };
          } else {
            return {
              ...block,
              id: newBlockId
            };
          }
        });
      }

      const newNode = {
        ...nodeToDup,
        id: newId,
        position: {
          x: nodeToDup.position.x,
          y: nodeToDup.position.y + 400,
        },
        data: {
          ...nodeToDup.data,
          id: newId,
          title: newTitle,
          blocks: cloneBlocks(nodeToDup.data.blocks),
          isDarkMode
        }
      };
      
     
      setConfigPanelOpen(false);
      setEditingNodeId(newNodeId);
      setSelectedNodeId(newNodeId);
     
      return [
        ...nodes.map(n =>
          n.id === nodeId ? { ...n, selected: false } : n
        ),
        newNode
      ];

    });
    return newNodeId;
  }, []);

  // ReactFlow helpers
  const onNodesChange = useCallback(
    (changes) => {
      setNodes((nds) => applyNodeChanges(changes, nds)); 
    },
    []
  );

  const onEdgesChange = useCallback(
    (changes) => setEdges((eds) => applyEdgeChanges(changes, eds)),
    []
  );

  const onConnect = useCallback(
    (params) => {
      // Update node data with the new target 

      if(params.sourceHandle === 'handle-'+params.source+'-all'){
         
        // Remove all edges that have this source
        setEdges(eds => eds.filter(e => e.source !== params.source));
        
        // Update the source node to set all blocks to target the new target
        setNodes(prevNodes => {
          return prevNodes.map(node => {
            if(node.id === params.source){
              const updatedBlocks = node.data.blocks.map(block => {
                if(block.type === 'quiz' && Array.isArray(block.options)){
                  const updatedOptions = block.options.map(option => {
                    if(option.destinationType === 'step'){
                      return { ...option, target: params.target };
                    }
                    return option;
                  });
                  return { ...block, options: updatedOptions };
                }
                if(block.type === 'button' && block.destinationType === 'step'){
                  return { ...block, target: params.target };
                }
                if(block.type === 'loading' && block.destinationType === 'step'){
                  return { ...block, target: params.target };
                }
                return block;
              });
              
              return {
                ...node,
                data: {
                  ...node.data,
                  blocks: updatedBlocks,
                  // Preserve functions
                  duplicateNode,
                  removeNode,
                  setNodes,
                  setEditingNodeId,
                  setConfigPanelOpen,
                  isDarkMode
                }
              };
            }
            return node;
          });
        });
        
        // Create new edges for all blocks that target steps
        setTimeout(() => {
          const sourceNode = nodes.find(n => n.id === params.source);
          if(sourceNode){
            const newEdges = [];
            
            sourceNode.data.blocks.forEach(block => {
              if(block.type === 'quiz' && Array.isArray(block.options)){
                block.options.forEach(option => {
                  if(option.destinationType === 'step'){
                    newEdges.push({
                      id: `e-${option.id}`,
                      source: params.source,
                      sourceHandle: option.id,
                      target: params.target,
                      type: 'default',
                      markerMid: { type: 'arrowclosed' },
                    });
                  }
                });
              }
              if(block.type === 'button' && block.destinationType === 'step'){
                newEdges.push({
                  id: `e-${block.id}`,
                  source: params.source,
                  sourceHandle: block.id,
                  target: params.target,
                  type: 'default',
                  markerMid: { type: 'arrowclosed' },
                });
              }
              if(block.type === 'loading' && block.destinationType === 'step'){
                newEdges.push({
                  id: `e-${block.id}`,
                  source: params.source,
                  sourceHandle: block.id,
                  target: params.target,
                  type: 'default',
                  markerMid: { type: 'arrowclosed' },
                });
              }
            });
            
            setEdges(eds => [...eds, ...newEdges]);
          }
        }, 0);
        
        return;
      }
  
      setEdges(eds => eds.filter(e => e.id !== `e-${params.sourceHandle}`));
    
     
        // Add a new edge with the given source and target
        setEdges((eds) =>
          addEdge(
            {
              id: `e-${params.sourceHandle}`,
              source: params.source,
              target: params.target,
              sourceHandle: params.sourceHandle,
              ...params
            },
            eds
          )
        );
     
 
       
        
        // Update nodes with proper state update pattern
        setNodes(prevNodes => {
          const updatedNodes = prevNodes.map(node => {
            if(node.id === params.source){

            
              if(params.source === 'start-step'){

                setSettingsFunnel({
                  ...settingsFunnel,
                  startStep: {
                    ...settingsFunnel.startStep,
                    target: params.target,
                  }
                  
                });
                
                return {
                  ...node,
                  data: {
                    ...node.data,
                    target: params.target,
                    isDarkMode
                  }
                };
              }
             
              const updatedBlocks = node.data.blocks.map(block => {
                if(block.type === 'quiz' && block.options.some(option => option.destinationType === 'step')){
                  const updatedOptions = block.options.map(option => {
                    if(option.destinationType === 'step' && option.id === params.sourceHandle){
                     

                      return { ...option, target: params.target };
                    }
                    return option;
                  });
                  return { ...block, options: updatedOptions };
                }
                if(block.type === 'button' && block.destinationType === 'step' && block.id === params.sourceHandle){
                 
                  return { ...block, target: params.target };
                }
                if(block.type === 'loading' && block.destinationType === 'step' && block.id === params.sourceHandle){
                 

                  return { ...block, target: params.target };
                }
                return block;
              });
              
              return {
                ...node,
                data: {
                  ...node.data,
                  blocks: updatedBlocks,
                  // Preserve functions
                  duplicateNode,
                  removeNode,
                  setNodes,
                  setEditingNodeId,
                  setConfigPanelOpen,
                  isDarkMode
                }
              };
            }
            return node;
          });
         
          return updatedNodes;
        });
      
      
       
       
       
       
    },
    [nodes] // Include nodes in dependency array to get current state
  );

  const removeEdge = useCallback((edgeId) => {
    setEdges(eds => eds.filter(e => e.id !== edgeId));
  }, []);

  // Fetch from DB
  useEffect(() => {
    
    async function fetchFunnel() {
      setLoading(true);
      const { data, error } = await supabase
        .from('funnels')
        .select('*')
        .eq('uuid', id)
        .single();

      if (error) {
        console.error('Erro ao carregar funil:', error.message);
      } else {
        setFunnel(data);
 
        setSettingsFunnel(JSON.parse(data.settings) || {logo: null, logoWidth: 20, startStep: {target: null, position: {x: 0, y: 0}}});
         
         
      }
      
      if (data && data.funnel && data.settings) {

        const settings = JSON.parse(data.settings) || {logo: null, logoWidth: 20, startStep: {target: null, position: {x: 0, y: 0}}};

       
        let steps = typeof data.funnel === 'string' ? JSON.parse(data.funnel) : data.funnel;
        if (!Array.isArray(steps)) steps = [];
        const generatedNodes = steps.map((step, idx) => ({
          id: step.id || `step-${idx}`,
          type: 'step',
          position: step.position && typeof step.position.x === 'number' && typeof step.position.y === 'number'
            ? { x: step.position.x, y: step.position.y }
            : { x: 60 + idx * 400, y: 100 },
          data: { ...step, title: step.title || `Etapa ${idx + 1}`,
          duplicateNode, removeNode, setNodes, setEditingNodeId, setConfigPanelOpen,
          isDarkMode
          }
        }));
        // Edges logic
 
        setNodes(generatedNodes);
        const generatedEdges = [];

       const startStepTarget = settings?.startStep?.target || generatedNodes[0].id;

       settings.startStep.target = startStepTarget;
        
        generatedNodes.push({
          id: 'start-step',
          type: 'step',
          position: settings?.startStep?.position || { x: generatedNodes[0].position.x - 600, y: generatedNodes[0].position.y },
          data: {
            title: 'Início', 
            data: {
              id: 'start-step',
             
            },
            isDarkMode

          },
           
        });
 
        console.log(settings?.startStep?.target)

        generatedEdges.push({
          id: `e-start-step`,
          source: 'start-step',
          sourceHandle: 'start-step',
          target: startStepTarget,
          type: 'default',
          markerMid: { type: 'arrowclosed' },
        });

        generatedNodes.forEach((step, idx) => {
          (step.data.blocks || []).forEach((block) => {
            if (block.type === 'quiz' && Array.isArray(block.options)) {
              block.options.forEach((option) => {
                
                if (option.destinationType === 'step' && option.target !== null) {
       
                  generatedEdges.push({
                    id: `e-${option.id}`,
                    source: step.id,
                    sourceHandle: option.id,
                    target: option.target,
                    type: 'default',
                    markerMid: { type: 'arrowclosed' },
                  });
                }
              });
            }
            if (block.type === 'button' && block.destinationType === 'step' && block.target !== null) {
           
              generatedEdges.push({
                id: `e-${block.id}`,
                source: step.id,
                sourceHandle: block.id,
                target: block.target, 
                type: 'default',
                markerMid: { type: 'arrowclosed' },
              });
            
            }
            if(block.type === 'loading' && block.destinationType === 'step' && block.target !== null){
            
              generatedEdges.push({
                id: `e-${block.id}`,
                source: step.id,
                sourceHandle: block.id,
                target: block.target, 
                type: 'default',
                markerMid: { type: 'arrowclosed' },
              });
            }
            if(block.type === 'quiz' && block.destinationType === 'url' && block.url !== null){
              generatedEdges.push({
                id: `e-${block.id}`,
                source: step.id,
                sourceHandle: block.id,
                target: block.url, 
                type: 'default',
                markerMid: { type: 'arrowclosed' },
              });
            }
          });
         }); 
      
         
        setEdges(generatedEdges);

      } else {
      
        setNodes([]);
        setEdges([]);
      }
      setLoading(false);
    }
    fetchFunnel();
  }, [id]);

  // Focus modal when it opens for keyboard shortcuts
  useEffect(() => {
    if (showDeleteModal) {
      const timer = setTimeout(() => {
        const modalElement = document.querySelector('[data-modal="delete-confirmation"]');
        if (modalElement) {
          modalElement.focus();
        }
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [showDeleteModal]);

  // Expose function to window for Header component to call
  useEffect(() => {
    window.openConfigFunnelModal = () => {
      setShowConfigFunnelModal(true);
    };
    window.openConfigFunnelDesign = () => {
      setShowConfigFunnelDesign(true);
    };
    return () => {
      delete window.openConfigFunnelModal;
      delete window.openConfigFunnelDesign;
    };
  }, []);

  const handlePublish = async () => {
    setIsPublishing(true);

   const {funnel_real, settings_real} = await project.handleSave();
 
    console.log(funnel_real, settings_real)
    const { error } = await supabase

        .from('funnels')
        .update({
          funnel_real: funnel_real,
          settings_real: settings_real,
          
        })
        .eq('uuid', id);
    setIsPublishing(false);
  }
  // Function to save funnel configuration to Supabase
  const handleSaveFunnelConfig = async (updatedFunnel) => {
    try {
      
      const { error } = await supabase
        .from('funnels')
        .update({
          title: updatedFunnel.title,
          slug: updatedFunnel.slug,
          description: updatedFunnel.description,
          script: script,
        })
        .eq('uuid', id);

      if (error) {
        console.error('Erro ao salvar configurações do funil:', error.message);
        alert('Erro ao salvar configurações. Tente novamente.');
      } else {
        // Update local state
        setFunnel(prev => ({
          ...prev,
          title: updatedFunnel.title,
          slug: updatedFunnel.slug,
          description: updatedFunnel.description,
          script: updatedFunnel.script,
        }));
        console.log('Configurações do funil salvas com sucesso!');
        console.log(updatedFunnel);
      }
    } catch (error) {
      console.error('Erro ao salvar configurações:', error);
      alert('Erro ao salvar configurações. Tente novamente.');
    }
  };

  // Function to save funnel design settings to Supabase
  const handleSaveFunnelDesign = async (updatedSettings) => {
    try {
      const { error } = await supabase
        .from('funnels')
        .update({
          settings: JSON.stringify(updatedSettings),
        })
        .eq('uuid', id);

      if (error) {
        console.error('Erro ao salvar design do funil:', error.message);
        alert('Erro ao salvar design. Tente novamente.');
      } else {
        // Update local state
        setSettingsFunnel(updatedSettings);
        console.log('Design do funil salvo com sucesso!');
      }
    } catch (error) {
      console.error('Erro ao salvar design:', error);
      alert('Erro ao salvar design. Tente novamente.');
    }
  };

  const project = useProjectLogic({
    getNodes: () => nodes,
    getEdges: () => edges,
    funnelId: id,
    getSettingsFunnel: () => settingsFunnel,
  }); 

  const handleEditNode = (nodeId) => setEditingNodeId(nodeId);

  function updateNode(nodeId, updatedNode) {
  
    const updatedNodes = nodes.map(node => 
      node.id === nodeId 
        ? { 
            ...node, 
            data: { 
              ...node.data, 
              ...updatedNode,
              // Ensure functions are preserved
              duplicateNode,
              removeNode,
              setNodes,
              setEditingNodeId,
              setConfigPanelOpen,
              isDarkMode,
            } 
          } 
        : node
    ); 
    
 
    setNodes(updatedNodes); 
 
  }

  // Função para deletar edge
  function handleDeleteEdge(edgeId) {

    const edge = edges.find(e => e.id === edgeId);

     
    nodes.map(node => { 
        if(edge.source === 'start-step'){
          removeEdge(edgeId);
          setSelectedEdge(null);
          return;
        }
      

      if(node.id === edge.source){
        node.data.blocks.map(block => {
          if(block.type === 'quiz' && block.options.some(option => option.target === edge.target)){
            block.options.map(option => {
              if(option.target === edge.target && `e-${option.id}` === edge.id){
                option.target = null;
              }
            });
            updateNode(node.id, node);
          }
          if(block.type === 'button' && block.destinationType === 'step' && block.target === edge.target && `e-${block.id}` === edge.id){
            block.target = null;
            updateNode(node.id, node);
          }
          if(block.type === 'loading' && block.destinationType === 'step' && block.target === edge.target && `e-${block.id}` === edge.id){
           
            block.target = null;
            updateNode(node.id, node);
          }
        });
      }
    });
    removeEdge(edgeId);
    setSelectedEdge(null);
    
  }
  
  // Just sets the selected edge!
  const onEdgeClick = (event, edge) => {
    event.stopPropagation();
    setSelectedEdge(edge.id);
  };

  function onHandleClick(event, handle) {
    console.log('handle', handle)
  }
  
  function updateNode2(nodeId, updatedNode) {
    updateNode(nodeId, updatedNode);

    
  }
  
   

  // Get the current editing node (this ensures we always have the latest data)
  const currentEditingNode = editingNodeId ? nodes.find(n => n.id === editingNodeId) : null;

  if (loading) return <Loading />;

  try{
    document.querySelector('.react-flow__attribution').style.display = 'none';
  }catch(e){
    console.log('error', e);
  }
  
  return (
    <div className={`flex w-full h-[100vh] ${isDarkMode ? 'bg-gray-900' : 'bg-gray-200'}`}>
      <Header onSave={project.handleSave} isSaving={project.isSaving} funnelId={id} title={funnel.title} isDarkMode={isDarkMode} toggleDarkMode={toggleDarkMode} handlePublish={handlePublish} isPublishing={isPublishing} />
     
      {/* Dark Mode Toggle */}
      
      <div className="flex flex-row items-center justify-end" 
           style={{position: 'absolute', top: 80, left: 10, zIndex: 10000}}>
        <button className={`${isDarkMode ? 'bg-gray-800 text-white hover:bg-gray-700' : 'bg-white text-gray-700 hover:bg-gray-50'} rounded-md p-2 shadow-md text-sm transition-colors`} 
                onClick={() => {
                  window.addStep && window.addStep();
                }}>
          + Adicionar Etapa
        </button>
      </div>
          
      <div className="flex-1 h-full" ref={reactFlowWrapper} style={{ position: 'relative' }}>
        <ReactFlowProvider>
          <ReactFlowCanvas
            nodes={nodes}
            edges={getStyledEdges(edges, selectedEdge)}
            onNodesChange={onNodesChange}
            onConnect={onConnect}
            nodeTypes={nodeTypes}
            onHandleClick={onHandleClick}
            onEdgeClick={onEdgeClick}
            selectedNodeId={selectedNodeId}
            setSelectedNodeId={setSelectedNodeId}
            setEditingNodeId={setEditingNodeId}
            setConfigPanelOpen={setConfigPanelOpen}
            handleEditNode={handleEditNode}
            selectedEdge={selectedEdge}
            handleDeleteEdge={handleDeleteEdge}
            isDarkMode={isDarkMode}
            setNodes={setNodes}
            removeNode={removeNode}
            updateNode={updateNode}
            duplicateNode={duplicateNode}
          />
          <EdgeLabelRenderer>
            {selectedEdge && (() => {
              const edge = edges.find(e => e.id === selectedEdge);
              if (!edge) return null;

              const sourceNode = nodes.find(n => n.id === edge.source);
              const targetNode = nodes.find(n => n.id === edge.target);
              if (!sourceNode || !targetNode) return null;

              // Defaults for width/height, adapt if your nodes are different!
              const sourceWidth = sourceNode.width || 220;
              const sourceHeight = sourceNode.height || 80;
              const targetWidth = targetNode.width || 220;
              const targetHeight = targetNode.height || 80;

              const sourceX = sourceNode.position.x + sourceWidth;
              const sourceY = sourceNode.position.y + sourceHeight / 2;
              const targetX = targetNode.position.x;
              const targetY = targetNode.position.y + targetHeight / 2;

              // Calculate center of the line manually
              const centerX = (sourceX + targetX) / 2;
              const centerY = (sourceY + targetY) / 2;

              return (
                <button
                  style={{
                    position: 'relative',
                    left: centerX,
                    top: centerY,
                    transform: 'translate(-50%, -50%)',
                    zIndex: 100000,
                    background: isDarkMode ? '#1f2937' : '#fff',
                    border: '2px solid #a21caf',
                    color: '#a21caf',
                    padding: '8px 10px',
                    borderRadius: '8px',
                    boxShadow: '0 2px 8px #a21caf33',
                    fontWeight: 'bold',
                    cursor: 'pointer',
                    pointerEvents: 'all', // <-- ESSENCIAL!

                  }}
                  onClick={e => {
                    e.stopPropagation();
                    handleDeleteEdge(edge.id);
                  }}
                >
                  <FaTrash />
                </button>
              );
            })()}
          </EdgeLabelRenderer>

        </ReactFlowProvider>
      </div>
      
      {editingNodeId && currentEditingNode && (
        <StepEditor
          node={currentEditingNode}
          funnel={{
            nodes,
            setNodes,
            edges,
            setEdges,
            loading,
            onNodesChange, 
            onConnect,
            removeEdge,
            removeNode,
            edgeCenter,
            setEdgeCenter,
            duplicateNode,
            setConfigPanelOpen,
          }}
          funnelNodes={nodes}
          data={currentEditingNode}
          onClose={() => setEditingNodeId(null)}
          setConfigPanelOpen={setConfigPanelOpen}
          configPanelOpen={configPanelOpen}
          onNodeChange={updateNode}
          updateNode={updateNode}
          setNodes={setNodes}
          updateNode2={updateNode2}
          nodes={nodes} 
          settingsFunnel={settingsFunnel}
          setSettingsFunnel={setSettingsFunnel}
          isDarkMode={isDarkMode}
        />
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center " style={{zIndex: 1000}}
          onClick={cancelDelete}
          onKeyDown={(e) => {
            if (e.key === 'Escape') cancelDelete();
            if (e.key === 'Enter') confirmDelete();
          }}
          tabIndex={0}
          data-modal="delete-confirmation"
        >
          <div 
            className={`${isDarkMode ? 'bg-gray-800 text-white' : 'bg-white text-gray-900'} rounded-lg p-6 max-w-md w-full mx-4 shadow-xl`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className={`flex items-center justify-center w-12 h-12 mx-auto ${isDarkMode ? 'bg-red-900' : 'bg-red-100'} rounded-full mb-4`}>
              <FaTrash className="text-red-600 text-xl" />
            </div>
            <h3 className={`text-lg font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'} text-center mb-2`}>
              Excluir Etapa
            </h3>
            <p className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-500'} text-center mb-6`}>
              Tem certeza que deseja excluir esta etapa? Esta ação não pode ser desfeita e todas as conexões com esta etapa serão removidas.
            </p>
            <div className="flex gap-3">
              <button
                onClick={cancelDelete}
                className={`flex-1 px-4 py-2 text-sm font-medium ${isDarkMode ? 'text-gray-300 bg-gray-700 border-gray-600 hover:bg-gray-600' : 'text-gray-700 bg-white border-gray-300 hover:bg-gray-50'} border rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500`}
              >
                Cancelar
              </button>
              <button
                onClick={confirmDelete}
                className="flex-1 px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
              >
                Excluir
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Config Funnel Modal */}
      <ConfigFunnelModal 
        funnel={funnel}
        open={showConfigFunnelModal}
        onClose={() => setShowConfigFunnelModal(false)}
        onSave={handleSaveFunnelConfig}
        isDarkMode={isDarkMode}
      />

      {/* Design Funnel Modal */}
      {showConfigFunnelDesign && (
        <ConfigFunnelDesign
          funnel={funnel}
          open={showConfigFunnelDesign}
          onClose={() => setShowConfigFunnelDesign(false)}
          onSave={handleSaveFunnelDesign}
          settingsFunnel={settingsFunnel}
          setSettingsFunnel={setSettingsFunnel}
          isDarkMode={isDarkMode}
        />
        
      )}

    </div>
  );
}
